/**
 * What the model is told (§14, §19).
 *
 * The model holds no state (§1, rule 2), so everything it needs arrives in
 * one self-describing context built here, from state, by the sim. The
 * server turns this into words; it never reaches back into the world.
 *
 * Two halves:
 *
 *  - The briefing: who he is, what he knows, what the numbers are. The
 *    model is told, never asked to recall (§11).
 *  - The manifest: what exists. Places, people, times, the closed vocab
 *    of outcomes and predicates. "The model can only promise things it
 *    knows exist. Don't validate the output — constrain the input" (§14).
 *
 * Nothing hidden from the player by design crosses here as a label:
 * `opening.solvability` is never read (§6).
 */

import { type CanonRecord, type Content, loadContent } from './content.js';
import { conversationMode } from '../engine/conversation.js';
import { PREDICATES } from './proposition.js';
import { assembleBriefing, type Briefing, type Topic } from '../engine/retrieval.js';
import { npcsAt } from './presence.js';
import { dayOfWeek, isBefore, timeOf } from './time.js';
import { TUNING } from './tuning.js';
import type {
  Appointment,
  BelievedEvent,
  EntityRef,
  Event,
  Flaw,
  ImpressionPermanence,
  ImpressionSource,
  Injury,
  LocationId,
  MemoryTrigger,
  NpcId,
  Predicate,
  Stats,
  TranscriptLine,
  WorldState,
  WorldTime,
} from './types/index.js';
import { needsStatus } from './needs.js';

export type RenderKind = 'opener' | 'talk' | 'scene' | 'digest';

/** Canonical definition lives on `WorldState` (`sim/types/world.ts`), since
 * the transcript is state now, not something only assembled for the model. */
export type { TranscriptLine };

export interface ManifestPlace {
  readonly id: LocationId;
  readonly name: string;
  readonly kind: string;
  readonly description: string;
  /** Already on the player's map. Telling them about a place they don't know puts it there (§16). */
  readonly knownToPlayer: boolean;
}

export interface ManifestPerson {
  readonly id: NpcId;
  readonly name: string;
  readonly role: string;
  readonly here: boolean;
  /** The player has met them. A stranger's name is not the player's to know yet. */
  readonly knownToPlayer: boolean;
}

export interface TimeAnchor {
  readonly label: string;
  readonly time: WorldTime;
}

/** The judgment-call triggers. The sim classifies the rest from event shape (§11). */
export const MODEL_TRIGGERS: readonly MemoryTrigger[] = ['betrayal', 'help_when_exposed', 'flaw'];

export interface Manifest {
  readonly places: readonly ManifestPlace[];
  readonly people: readonly ManifestPerson[];
  readonly factions: readonly { readonly id: string; readonly name: string }[];
  readonly situations: readonly { readonly id: string; readonly kind: string }[];
  /** Recent events, by id, so `memory` and `said`/`did` can point at real ones. */
  readonly events: readonly Event[];
  readonly predicates: readonly Predicate[];
  readonly triggers: readonly MemoryTrigger[];
  readonly purposes: readonly string[];
  readonly time: {
    readonly now: WorldTime;
    readonly weekday: string;
    readonly anchors: readonly TimeAnchor[];
  };
  readonly caps: {
    readonly relationshipPerScene: number;
    readonly stancePerScene: number;
  };
}

export interface PlayerSummary {
  readonly name: string;
  readonly origin: string;
  readonly stats: Stats;
  readonly flaws: readonly Flaw[];
  readonly money: number;
  readonly injuries: readonly Injury[];
  readonly hunger: string;
  readonly fatigue: string;
  readonly dependent: NpcId;
}

export interface ConversationContext {
  /**
   * `opener` is him speaking first, before the player has said anything
   * (§14): free, no outcomes, cheap tier. The other two are exchanges.
   */
  readonly kind: 'opener' | 'talk' | 'scene';
  readonly npc: Briefing;
  readonly player: PlayerSummary;
  readonly here: {
    readonly id: LocationId;
    readonly name: string;
    readonly description: string;
    /** Everyone else at this door. */
    readonly present: readonly NpcId[];
  };
  readonly manifest: Manifest;
  /** Every id the server may need a name for. */
  readonly names: Readonly<Record<string, string>>;
  readonly transcript: readonly TranscriptLine[];
  readonly playerText: string;
  /** What this exchange costs. Two minutes on the street or an hour in a booth (§9). */
  readonly minutes: number;
  /** Canon facts, only when the topic was directly raised (CLAUDE.md). */
  readonly canon: readonly string[];
  /** The nearest thing between you on a deadline. A man with a problem doesn't want to chat (§14). */
  readonly clock: { readonly what: string; readonly by: WorldTime } | null;
}

/**
 * What one person made of something (§11). The digest's subject in V0: not
 * what happened — the player was there for that — but who found out and what
 * it did to them. Bonds are how people find out without being present, so
 * this is the layer the fan-out feeds.
 */
export interface Reaction {
  readonly npc: NpcId;
  readonly name: string;
  /** Saw it, or was told, and by whom. */
  readonly source: ImpressionSource;
  readonly believed: BelievedEvent;
  readonly permanence: ImpressionPermanence;
  /** They were not there; they are bonded to someone who was (§11). */
  readonly throughBond: boolean;
}

export interface DigestContext {
  readonly kind: 'digest';
  readonly day: number;
  readonly weekday: string;
  readonly player: PlayerSummary;
  /** What people made of the day. The digest is about these. */
  readonly reactions: readonly Reaction[];
  /** Context for the reactions, not the subject of the telling. */
  readonly events: readonly Event[];
  readonly tomorrow: readonly Appointment[];
  readonly names: Readonly<Record<string, string>>;
  readonly manifest: Pick<Manifest, 'places' | 'people' | 'time'>;
}

export type RenderContext = ConversationContext | DigestContext;

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ---------------------------------------------------------------------------
// Names and the manifest
// ---------------------------------------------------------------------------

export function nameTable(state: WorldState): Record<string, string> {
  const names: Record<string, string> = { player: state.player.name };
  for (const npc of Object.values(state.npcs)) names[npc.id] = npc.name;
  for (const stub of Object.values(state.stubs)) names[stub.id] = stub.name;
  for (const loc of Object.values(state.locations)) names[loc.id] = loc.name;
  for (const f of Object.values(state.factions)) names[f.id] = f.name;
  for (const s of Object.values(state.situations)) names[s.id] = s.kind;
  for (const s of Object.values(state.secrets)) names[s.id] = s.content;
  return names;
}

function anchors(now: WorldTime): TimeAnchor[] {
  const out: TimeAnchor[] = [{ label: 'now', time: now }];
  const tonight = timeOf(now.day, 20, 0);
  if (isBefore(now, tonight)) out.push({ label: 'tonight', time: tonight });
  out.push(
    { label: 'tomorrow morning', time: timeOf(now.day + 1, 9, 0) },
    { label: 'tomorrow afternoon', time: timeOf(now.day + 1, 14, 0) },
    { label: 'tomorrow night', time: timeOf(now.day + 1, 20, 0) },
    { label: 'the day after tomorrow', time: timeOf(now.day + 2, 12, 0) },
  );
  return out;
}

export function manifest(state: WorldState, content: Content = loadContent()): Manifest {
  const here = state.player.position.at;
  const present = here ? new Set(npcsAt(state, here)) : new Set<NpcId>();
  const cards = new Map(content.locations.map((c) => [c.id, c]));
  return {
    places: Object.values(state.locations).map((l) => ({
      id: l.id,
      name: l.name,
      kind: l.kind,
      description: cards.get(l.id)?.description ?? '',
      knownToPlayer: state.player.knownLocations.includes(l.id),
    })),
    people: Object.values(state.npcs)
      .filter((n) => n.status !== 'dead')
      .map((n) => ({
        id: n.id,
        name: n.name,
        role: n.role,
        here: present.has(n.id),
        knownToPlayer: n.read.acquaintance === 'known',
      })),
    factions: Object.values(state.factions)
      .filter((f) => f.status === 'active')
      .map((f) => ({ id: f.id, name: f.name })),
    situations: Object.values(state.situations)
      .filter((s) => s.state === 'surfaced')
      .map((s) => ({ id: s.id, kind: s.kind })),
    events: state.memory.events.slice(-TUNING.render.recentEvents),
    predicates: PREDICATES,
    triggers: MODEL_TRIGGERS,
    purposes: ['meet', 'deliver', 'intercede', 'hold', 'come_find_me'],
    time: { now: state.time, weekday: WEEKDAYS[dayOfWeek(state.time)] ?? '', anchors: anchors(state.time) },
    caps: { relationshipPerScene: TUNING.relationship.perSceneCap, stancePerScene: TUNING.stance.perSceneCap },
  };
}

function playerSummary(state: WorldState): PlayerSummary {
  const needs = needsStatus(state);
  return {
    name: state.player.name,
    origin: state.player.origin.summary,
    stats: state.player.stats,
    flaws: state.player.flaws,
    money: state.player.money,
    injuries: state.player.injuries,
    hunger: needs.hunger,
    fatigue: needs.fatigue,
    dependent: state.player.dependent,
  };
}

// ---------------------------------------------------------------------------
// Topic and canon, from what the player said
// ---------------------------------------------------------------------------

const MONEY = /\$|\bmoney\b|\bcash\b|\bowe|\bpay|\bdebt\b|\bdollars?\b|\bbucks\b/i;
const VIOLENCE = /\bfight|\bhit\b|\bhurt\b|\bbeat|\bgun\b|\bknife\b|\bkill/i;

function words(s: string): string[] {
  return s.toLowerCase().split(/[^a-z']+/).filter((w) => w.length > 2);
}

/** Retrieval topic from the player's words: names mentioned, money, violence. Deterministic. */
export function topicFrom(state: WorldState, text: string): Topic {
  const said = new Set(words(text));
  const about: EntityRef[] = [];
  for (const npc of Object.values(state.npcs)) {
    if (words(npc.name).some((w) => said.has(w))) about.push({ kind: 'npc', id: npc.id });
  }
  for (const loc of Object.values(state.locations)) {
    if (words(loc.name).some((w) => w !== 'the' && said.has(w))) about.push({ kind: 'location', id: loc.id });
  }
  const tags: string[] = [];
  if (MONEY.test(text)) tags.push('money');
  if (VIOLENCE.test(text)) tags.push('violence');
  return { tags, about };
}

/**
 * Was the vanished crew directly raised (CLAUDE.md)? By name only: the crew's
 * name or one of its people. Nothing is volunteered on a hunch.
 */
export function canonRaised(text: string, canon: CanonRecord): boolean {
  const said = new Set(words(text));
  const names = [canon.crew.name, ...canon.crew.people.flatMap((p) => [p.name])];
  return names.some((n) => words(n).some((w) => w !== 'the' && w !== 'crew' && said.has(w)));
}

/**
 * What the block knows when the subject comes up. V0 has no hook and no one
 * who was close to it, so only tier 1 ever crosses: "half the district will
 * tell you this for free". Higher tiers wait on the V1 generator.
 */
export function canonForScene(state: WorldState, text: string, content: Content = loadContent()): readonly string[] {
  if (!canonRaised(text, content.canon)) return [];
  // V0: `state.hook` is null and no one on the block was close to it (§20).
  const tier = 1;
  void state;
  return content.canon.tiers.filter((t) => t.tier <= tier).flatMap((t) => t.facts);
}

// ---------------------------------------------------------------------------
// The two contexts
// ---------------------------------------------------------------------------

export interface ConversationRequest {
  readonly npc: NpcId;
  readonly playerText: string;
  readonly transcript?: readonly TranscriptLine[];
  readonly minutes: number;
}

function nearestClock(state: WorldState, npcId: NpcId): ConversationContext['clock'] {
  let best: ConversationContext['clock'] = null;
  const consider = (what: string, by: WorldTime | null) => {
    if (by === null || isBefore(by, state.time)) return;
    if (best === null || isBefore(by, best.by)) best = { what, by };
  };
  for (const f of Object.values(state.memory.facts)) {
    if (f.kind !== 'obligation' || f.settled) continue;
    const between = (f.from === 'player' && f.to === npcId) || (f.to === 'player' && f.from === npcId) || f.to === npcId;
    if (!between) continue;
    const what = f.substance.kind === 'money' ? `$${f.substance.amount} owed by ${f.from} to ${f.to}` : `${f.substance.description} (owed by ${f.from} to ${f.to})`;
    consider(what, f.deadline);
  }
  for (const a of Object.values(state.appointments)) {
    if (a.status.kind !== 'pending' || !a.participants.includes(npcId) || !a.participants.includes('player')) continue;
    consider(`meeting at ${a.place}`, a.time);
  }
  return best;
}

/** Everything the model is told for one exchange with one person. Null if he isn't here. */
export function renderContext(state: WorldState, req: ConversationRequest, content: Content = loadContent()): ConversationContext | null {
  const here = state.player.position.at;
  if (here === null) return null;
  const npc = state.npcs[req.npc];
  if (!npc || !npcsAt(state, here).includes(req.npc)) return null;
  const briefing = assembleBriefing(state, req.npc, topicFrom(state, req.playerText));
  if (!briefing) return null;
  const location = state.locations[here];
  if (!location) return null;
  const card = content.locations.find((c) => c.id === here);
  return {
    kind: conversationMode(state, req.npc) === 'scene' ? 'scene' : 'talk',
    npc: briefing,
    player: playerSummary(state),
    here: {
      id: here,
      name: location.name,
      description: card?.description ?? '',
      present: npcsAt(state, here).filter((id) => id !== req.npc),
    },
    manifest: manifest(state, content),
    names: nameTable(state),
    transcript: req.transcript ?? [],
    playerText: req.playerText,
    minutes: req.minutes,
    canon: canonForScene(state, req.playerText, content),
    clock: nearestClock(state, req.npc),
  };
}

/**
 * Him speaking first (§14). Same briefing, no player line, no time cost: a
 * conversation opens with the other person, not with a blank box. With the
 * model off the sim's own line stands in — `talk` already carries it.
 */
export function openerContext(state: WorldState, npc: NpcId, content: Content = loadContent()): (ConversationContext & { readonly kind: 'opener' }) | null {
  const ctx = renderContext(state, { npc, playerText: '', minutes: 0 }, content);
  return ctx === null ? null : { ...ctx, kind: 'opener' };
}

/** Everything that happened on `day`, for one batched call (§19). Conversations are not news. */
export function digestContext(state: WorldState, day: number, content: Content = loadContent()): DigestContext {
  const from = { day, minute: 0 };
  const to = { day: day + 1, minute: 0 };
  const onDay = (t: WorldTime) => !isBefore(t, from) && isBefore(t, to);
  const events = state.memory.events.filter((e) => onDay(e.at) && e.type !== 'conversation');

  // What people made of it. Permanent first: those are the ones that will
  // still be true in a month (§11). A day nobody reacted to gets no digest.
  const reactions: Reaction[] = Object.values(state.memory.impressions)
    .filter((i) => onDay(i.formedOn) && i.believed.type !== 'conversation' && state.npcs[i.npc]?.status !== 'dead')
    .sort((a, b) => Number(b.permanence.tier === 'permanent') - Number(a.permanence.tier === 'permanent'))
    .slice(0, TUNING.render.digestReactions)
    .map((i) => ({
      npc: i.npc,
      name: state.npcs[i.npc]?.name ?? i.npc,
      source: i.source,
      believed: i.believed,
      permanence: i.permanence,
      throughBond: i.tags.includes('bond'),
    }));
  const tomorrow = Object.values(state.appointments).filter(
    (a) => a.status.kind === 'pending' && a.participants.includes('player') && a.time.day === day + 1,
  );
  const m = manifest(state, content);
  return {
    kind: 'digest',
    day,
    weekday: WEEKDAYS[dayOfWeek(from)] ?? '',
    player: playerSummary(state),
    reactions,
    events,
    tomorrow,
    names: nameTable(state),
    manifest: { places: m.places, people: m.people, time: m.time },
  };
}
